import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, CheckCircle2, BookOpen, Building2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Chip } from "@/components/StepBits";
import { Field, TextArea, SubmitButton } from "@/components/FormBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUESTING_FOR = ["Myself", "My family / community", "Professional practice"];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const GuideForm = () => {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", requesting_for: "", reason: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please add your name and email.");
    setBusy(true);
    try {
      await axios.post(`${API}/threads-requests`, { type: "guide", ...form });
      setDone(true);
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="guide-success" className="rounded-[40px] bg-creamdark border border-ochre/25 p-10 text-center">
        <CheckCircle2 className="h-10 w-10 text-terracotta mx-auto mb-5" />
        <h3 className="font-display font-light text-3xl text-charcoal">Thanks for reaching out.</h3>
        <p className="mt-4 text-earth/85 leading-relaxed">We've received your request for the Finding the Threads Guide & Reflection. A member of the Ngawilyi team will be in touch.</p>
      </div>
    );
  }

  return (
    <form data-testid="guide-request-form" onSubmit={submit} className="rounded-[40px] bg-creamdark border border-ochre/25 p-8 sm:p-12">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Name" testid="gf-name-input" value={form.name} onChange={set("name")} required />
        <Field label="Email" testid="gf-email-input" type="email" value={form.email} onChange={set("email")} required />
        <Field label="Organisation" testid="gf-org-input" optional value={form.organisation} onChange={set("organisation")} />
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-earth">I'm requesting this for</span>
          <div className="flex flex-wrap gap-2.5 pt-1">
            {REQUESTING_FOR.map((t) => (
              <Chip key={t} testid={`gf-for-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.requesting_for === t} onClick={() => setForm((f) => ({ ...f, requesting_for: f.requesting_for === t ? "" : t }))}>{t}</Chip>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <TextArea label="What has brought you to Finding the Threads?" testid="gf-reason-textarea" optional rows={3} value={form.reason} onChange={set("reason")} />
      </div>
      <div className="mt-6">
        <TextArea label="Message" testid="gf-message-textarea" optional rows={3} value={form.message} onChange={set("message")} />
      </div>
      <div className="mt-8">
        <SubmitButton testid="gf-submit-btn" busy={busy}>Request the Guide & Reflection</SubmitButton>
      </div>
    </form>
  );
};

const TrainingForm = () => {
  const [form, setForm] = useState({ name: "", organisation: "", role: "", email: "", participants: "", workforce_area: "", training_goals: "", preferred_timing: "", additional_info: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please add your name and email.");
    setBusy(true);
    try {
      await axios.post(`${API}/threads-requests`, { type: "training", ...form });
      setDone(true);
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="training-success" className="rounded-[40px] bg-creamdark border border-ochre/25 p-10 text-center">
        <CheckCircle2 className="h-10 w-10 text-terracotta mx-auto mb-5" />
        <h3 className="font-display font-light text-3xl text-charcoal">Thanks for reaching out.</h3>
        <p className="mt-4 text-earth/85 leading-relaxed">We've received your training enquiry. A member of the Ngawilyi team will be in touch to yarn about the day.</p>
      </div>
    );
  }

  return (
    <form data-testid="training-request-form" onSubmit={submit} className="rounded-[40px] bg-creamdark border border-ochre/25 p-8 sm:p-12">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Name" testid="tf-name-input" value={form.name} onChange={set("name")} required />
        <Field label="Organisation" testid="tf-org-input" value={form.organisation} onChange={set("organisation")} required />
        <Field label="Role" testid="tf-role-input" optional value={form.role} onChange={set("role")} />
        <Field label="Email" testid="tf-email-input" type="email" value={form.email} onChange={set("email")} required />
        <Field label="Approximate number of participants" testid="tf-participants-input" optional value={form.participants} onChange={set("participants")} />
        <Field label="Workforce / service area" testid="tf-workforce-input" optional value={form.workforce_area} onChange={set("workforce_area")} placeholder="e.g. child protection, education, ACCO" />
        <Field label="Preferred timing / date if known" testid="tf-timing-input" optional value={form.preferred_timing} onChange={set("preferred_timing")} />
      </div>
      <div className="mt-6">
        <TextArea label="What would you like your team to gain from the day?" testid="tf-goals-textarea" optional rows={3} value={form.training_goals} onChange={set("training_goals")} />
      </div>
      <div className="mt-6">
        <TextArea label="Additional information" testid="tf-additional-textarea" optional rows={3} value={form.additional_info} onChange={set("additional_info")} />
      </div>
      <div className="mt-8">
        <SubmitButton testid="tf-submit-btn" busy={busy}>Request Organisational Training</SubmitButton>
      </div>
    </form>
  );
};

const Threads = () => (
  <div data-testid="threads-page">
    <PageHero
      kicker="Threads"
      title={"Finding the Threads"}
      lede="Reconnection, identity and the stories that sit between the records. Finding the Threads is a narrative-informed approach to exploring reconnection, identity, Country, movement and the gaps that can exist within Aboriginal family stories."
    />

    {/* DISCONNECTION STORIES */}
    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <Kicker>When gaps become stories</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Sometimes those gaps can become <span className="italic text-terracotta">individual stories.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            Finding the Threads creates space to step outside the disconnection story and become curious about
            what happened around it. It brings together narrative practice, historical context, records, Country,
            community knowledge and reflection to explore the threads that remained — including those colonisation,
            removal, displacement and Western systems made harder to see.
          </p>
        </Reveal>
        <div className="space-y-4">
          {["I don't know enough.", "I'm disconnected.", "There are too many missing pieces.", "I should already know this."].map((s, i) => (
            <Reveal key={s} delay={i * 0.07}>
              <div className="rounded-3xl bg-blush/40 border border-blush px-8 py-6 font-display italic text-2xl sm:text-3xl font-light text-earth">"{s}"</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* WHAT RECONNECTION CAN MEAN */}
    <section className="px-6 py-14 sm:py-20 bg-charcoal text-cream grain relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-terracotta/25 organic-blob blur-3xl" />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <Kicker light>Looking differently</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08]">
            Reconnection isn't about filling every gap — or finding one document that tells someone <span className="italic text-ochre">who they are.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-cream/80 leading-relaxed mb-6">It can mean:</p>
          <ul className="space-y-4">
            {[
              "Understanding movement as survival rather than simply disconnection",
              "Hearing stories held by family and community",
              "Understanding what was happening on Country at the time",
              "Returning to places",
              "Looking differently at records",
              "Noticing what knowledge, relationships and connections survived",
            ].map((t) => (
              <li key={t} className="flex gap-4 items-start text-cream/85 leading-relaxed">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ochre" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>

    {/* GUIDE & REFLECTION */}
    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12 max-w-3xl">
          <Kicker>The resource</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Finding the Threads <span className="italic text-terracotta">Guide & Reflection.</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-earth">
            A supported space to explore a person's own journey of reconnection — bringing together practical ways
            of tracing information with narrative reflection, creating space to think about not only what is found,
            but what those discoveries mean to the person.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-6">
          {[
            ["Tracing the threads", "Country, birth places, movement, records, archives, stories and community knowledge."],
            ["Understanding what happened around them", "Placing movement alongside colonisation, missions, removals, exemption systems, foster care and other historical and systemic forces."],
            ["Stepping outside the disconnection story", "Exploring stories such as \u201cnot knowing enough,\u201d \u201cnot belonging\u201d or \u201cbeing disconnected\u201d — and becoming curious about what contributed to them."],
            ["Re-authoring connection", "Noticing survival, resistance, relationships, knowledge and connections that continued despite disruption."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.07}>
              <div data-testid={`threads-guide-area-${i}`} className="rounded-[36px] bg-creamdark border border-ochre/25 p-9 h-full transition-transform duration-500 hover:-translate-y-2">
                <span className="font-display italic text-lg text-terracotta/60">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-3xl font-light text-charcoal leading-snug">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-earth/85">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 rounded-[40px] bg-blush/40 border border-blush p-9 sm:p-12 flex flex-wrap items-center justify-between gap-8">
            <div className="max-w-xl">
              <h3 className="font-display font-light text-3xl text-charcoal">Request the Guide & Reflection</h3>
              <p className="mt-3 text-earth/85 leading-relaxed">
                The Guide & Reflection is available by request, so the resource can be shared with context around
                its purpose and intended use.
              </p>
            </div>
            <button type="button" data-testid="request-guide-btn" onClick={() => scrollTo("guide-form")} className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
              Request Finding the Threads Guide & Reflection <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>

    {/* ORGANISATIONAL TRAINING */}
    <section className="px-6 py-14 sm:py-20 bg-creamdark grain relative overflow-hidden">
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-terracotta/10 organic-blob-alt blur-3xl" />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <Kicker>For organisations</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Bring Finding the Threads <span className="italic text-terracotta">to your organisation.</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
            Finding the Threads can be delivered as a one-day professional learning workshop for organisations
            wanting to strengthen practice around identity, reconnection and disrupted family stories.
          </p>
          <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
            The training isn't about teaching professionals to become experts in somebody else's identity. Instead,
            it explores how workers can walk alongside people in ways that recognise the person, their family and
            community as the knowledge-holders of their own stories.
          </p>
          <div className="mt-8 rounded-3xl bg-cream border border-ochre/30 px-7 py-5 flex items-center gap-4">
            <Building2 className="h-6 w-6 text-terracotta shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-earth"><strong className="text-charcoal">One-Day Organisational Training</strong> — tailored to your organisation, workforce and practice context.</p>
          </div>
          <button type="button" data-testid="request-training-btn" onClick={() => scrollTo("training-form")} className="group mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-all duration-300 hover:bg-terracotta hover:scale-105">
            Request Organisational Training <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-[40px] bg-cream border border-ochre/25 p-10 sm:p-12">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-6">The training can explore</p>
            <ul className="space-y-4">
              {[
                "The Finding the Threads approach",
                "Narrative practices for stepping outside stories of disconnection",
                "Understanding movement through survival and resistance",
                "Reading records alongside historical and colonial context",
                "Working respectfully with gaps, contradictions and missing information",
                "Country and community knowledge alongside written records",
                "Narrative and reflective questioning",
                "Practical application within everyday professional practice",
              ].map((t) => (
                <li key={t} className="flex gap-4 items-start text-earth leading-relaxed">
                  <BookOpen className="h-4 w-4 mt-1.5 shrink-0 text-terracotta" strokeWidth={1.75} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    {/* REQUEST FORMS */}
    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>Request</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">Start with a yarn.</h2>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div id="guide-form" className="scroll-mt-28">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-4">01 — Guide & Reflection</p>
              <GuideForm />
            </Reveal>
          </div>
          <div id="training-form" className="scroll-mt-28">
            <Reveal delay={0.1}>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-4">02 — Organisational Training</p>
              <TrainingForm />
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* PRACTICE NOTE */}
    <section className="px-6 pb-16 sm:pb-24 bg-cream">
      <Reveal>
        <div data-testid="threads-practice-note" className="max-w-5xl mx-auto rounded-[48px] bg-blush/40 border border-blush p-10 sm:p-16 grain relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-terracotta/12 organic-blob blur-2xl" />
          <div className="relative">
            <Kicker>Important practice note</Kicker>
            <p className="font-display font-light text-3xl sm:text-4xl leading-snug text-charcoal">
              Finding the Threads is <span className="italic text-terracotta">not about proving identity.</span>
            </p>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
              It does not determine, validate or decide somebody's Aboriginal identity. It creates space for
              reconnection, reflection and understanding — while recognising the histories and systems that have
              contributed to the gaps people may be trying to make sense of.
            </p>
          </div>
        </div>
      </Reveal>
    </section>

    <CTABand
      title="Healing through story. Strength through connection."
      text="Curious about how Finding the Threads could sit alongside Ngawilyi's other supports?"
      primary={{ to: "/request-support", label: "Request Support" }}
      secondary={{ to: "/approach", label: "Explore Our Approach" }}
    />
  </div>
);

export default Threads;
